import React, { useEffect, useState } from "react";
import Card from "../../Card/Card";
import {
  Calendar,
  CheckCircle,
  ChevronRight,
  Info,
  Clock,
  Gift,
  Upload,
} from "lucide-react";
import Button from "../../Button";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as journeyActions from "src/redux/slices/journeySlice";
import moment from "moment";
import { CycleItemType } from "src/types/journey.types";
import { COMPLETED_CYCLE_VISIT_STATUS } from "src/constants/journey";
import Shimmer from "src/components/common/Shimmer";
import { DOCUMENT_REVIEW_STATUS } from "src/constants/common";
import { DATE_FORMAT_STRING } from "src/constants/dataTimeFormats";
import analytics from "src/helpers/analytics";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";
import Alert from "../../Alert";

const CycleJourneyCard: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useAppDispatch();
  const { cycles, cycleDocuments } = useAppSelector((state) => state.journey);
  const areCyclesPresent = cycles?.data?.length;
  const { patient } = useAppSelector((state) => state.auth);
  const supportProgramKey = params["support-program-key"];
  const [vivitraCardType, setVivitraCardType] = useState<
    string | "EL" | "SH" | "RC" | ""
  >(""); // EL = Eligible, SH = Shipped, RC = Ready for Claim

  const getCycleDocuments = () => {
    const payload = {
      page: "1",
      record_meta_info: { visit_id: cycles?.data?.[0]?.patient_visit_id },
      patient_id: cycles?.data?.[0]?.patient_id,
    };
    new Promise((resolve, reject) => {
      dispatch(
        journeyActions.getCycleDocumentsRequest({ payload, resolve, reject }),
      );
    });
  };

  useEffect(() => {
    getCycleDocuments();
  }, []);

  const getCyclesData = () => {
    try {
      const payload = {
        patient_id: patient?.data?.patient_id,
        records_needed: 1,
      };
      new Promise((resolve, reject) => {
        dispatch(
          journeyActions.getCyclesRequest({
            resolve,
            reject,
            payload,
          }),
        );
      });
    } catch (error) {
      console.error("Error fetching cycles data", error);
    }
  };

  useEffect(() => {
    getCyclesData();
  }, []);

  const nextCycle: CycleItemType | undefined = cycles?.data?.find(
    (i) => i.visit_status === "N",
  );

  const completedCycles: CycleItemType[] = cycles?.data?.filter((i) =>
    COMPLETED_CYCLE_VISIT_STATUS.includes(i.visit_status),
  );

  const isNextCycleUpcoming: boolean =
    (moment(nextCycle?.rescheduled_date).isBefore(
      moment().format("YYYY-MM-DD HH:mm:ss"),
    ) ||
      moment(nextCycle?.rescheduled_date).isSame(
        moment().format("YYYY-MM-DD HH:mm:ss"),
      )) ??
    false;

  const nextCycleRequiresDocuments: boolean = !!cycles?.data?.find(
    (i) => i.visit_status === "VNU",
  );

  const isLastCycleNotAttended: boolean =
    cycles?.data?.[0]?.visit_status === "N" &&
    moment().diff(moment(cycles?.data?.[0]?.rescheduled_date), "hours") > 0;

  const isDocumentRejected: boolean = cycleDocuments?.data?.documents?.some(
    (i) =>
      i.record_meta_info?.report_status === DOCUMENT_REVIEW_STATUS.REJECTED,
  );

  const onPressYes = () => {
    // Event: ON_PRESS_YES_CLICKED
    analytics.trackEvent(EVENT_NAMES.ON_PRESS_YES_CLICKED, {
      screen_name: SCREEN_NAMES.HOME_SCREEN,
      support_program_key: supportProgramKey,
      card_title: "Treatment Journey",
    });

    const payload = {
      patient_visit_id: nextCycle?.patient_visit_id,
      visit_status: "VNU",
      updated_by: nextCycle?.patient_id,
    };
    const extra = {
      ...nextCycle,
    };

    const analyticsPayload = {
      screen_name: SCREEN_NAMES.HOME_SCREEN,
      support_program_key: supportProgramKey,
      card_title: "Treatment Journey",
      ...payload,
      ...extra,
    };

    const trackOnPressYesSuccess = () => {
      // Event: ON_PRESS_YES_SUCCESS
      analytics.trackEvent(EVENT_NAMES.ON_PRESS_YES_SUCCESS, analyticsPayload);
    };

    const trackOnPressYesError = (error: any) => {
      // Event: ON_PRESS_YES_ERROR
      analytics.trackEvent(EVENT_NAMES.ON_PRESS_YES_ERROR, {
        ...analyticsPayload,
        error: error?.message,
      });
    };

    try {
      new Promise((resolve, reject) => {
        dispatch(
          journeyActions.markAttendanceRequest({
            resolve,
            reject,
            payload,
            extra,
          }),
        );
      })
        .then((res) => {
          trackOnPressYesSuccess();
        })
        .catch((error) => {
          // Event: ON_PRESS_YES_ERROR
          trackOnPressYesError(error?.message);
        });
    } catch (error) {
      trackOnPressYesError(error);
      console.error("Error marking attendance:", error);
    }
  };

  const onPressNo = () => {
    // Event: ON_PRESS_NO_CLICKED
    analytics.trackEvent(EVENT_NAMES.ON_PRESS_NO_CLICKED, {
      screen_name: SCREEN_NAMES.HOME_SCREEN,
      support_program_key: supportProgramKey,
      card_title: "Treatment Journey",
      ...nextCycle,
    });

    navigate(
      `/app/${supportProgramKey}/journey-details/cycle-details/reschedule-cycle`,
      {
        state: {
          cycle: nextCycle,
        },
      },
    );
  };

  // VIVITRA CARD MODULE
  const isEligibleForVivitra =
    patient?.data?.patient_meta_info?.vivitra_eligible ?? 0;

  const vitraStatus = nextCycle?.visit_meta_info?.vivitra_status;
  const isFreeDrugsAvailable =
    nextCycle?.visit_meta_info?.is_free_drugs_available === "1";

  useEffect(() => {
    if (isFreeDrugsAvailable) {
      setVivitraCardType("EL");
    }

    if (isFreeDrugsAvailable && vitraStatus === "Ordered") {
      setVivitraCardType("SH");
    }

    if (
      ["Shipped", "Delivered"].includes(
        nextCycle?.visit_meta_info?.vivitra_status ?? "",
      )
    ) {
      setVivitraCardType("SH");
    }

    if (!nextCycle?.visit_meta_info?.vivitra_claimed) {
      const rescheduledDate = moment(nextCycle?.rescheduled_date);
      const isNextCycleUpcoming = rescheduledDate.diff(moment(), "hours") > 0;

      if (isNextCycleUpcoming) {
        setVivitraCardType("RC");
      }
    }
  }, [isFreeDrugsAvailable, nextCycle]);

  const claimFreeDrugCard = vivitraCardType !== "SH";

  const freeDrugCardTitle = () => {
    const visitInfo = nextCycle?.visit_meta_info;
    if (!visitInfo) return ""; // No data available

    const { primary_drugs = [], secondary_drugs = [] } = visitInfo;

    // Collect all free drugs (is_free === "1" or 1)
    const freeDrugs = [
      ...primary_drugs.filter(
        (drug: any) => drug.is_free === "1" || drug.is_free === 1,
      ),
      ...secondary_drugs.filter(
        (drug: any) => drug.is_free === "1" || drug.is_free === 1,
      ),
    ];

    // No free drugs found
    if (freeDrugs.length === 0) {
      return ""; // or you can return something like "No free drugs available"
    }

    // Handle singular vs plural
    return freeDrugs.length > 1
      ? "You have free drugs available for your next cycle"
      : "You have a free drug available for your next cycle";
  };

  const handleRightIconClick = () => {
    // Event: DOCTOR_DETAILS_CAPTURED
    analytics.trackEvent(
      EVENT_NAMES.TREATMENT_JOURNEY_CARD_RIGHT_ICON_CLICKED,
      {
        screen_name: SCREEN_NAMES.HOME_SCREEN,
        support_program_key: supportProgramKey,
        card_title: "Treatment Journey",
      },
    );

    // Navigate to journey details screen
    navigate(`/app/${supportProgramKey}/journey-details`, {
      state: {
        cycles: cycles,
        nextCycle: nextCycle,
        vivitraCardType: vivitraCardType,
        cycleDocuments: cycleDocuments,
        isEligibleForVivitra: isEligibleForVivitra,
      },
    });
  };

  const handleDocumentUpload = (isDocumentRejected: boolean) => {
    // Event: RECORD_UPLOAD_CLICKED
    analytics.trackEvent(EVENT_NAMES.RECORD_UPLOAD_CLICKED, {
      screen_name: SCREEN_NAMES.HOME_SCREEN,
      support_program_key: supportProgramKey,
      cycle_number: cycles?.data?.[0]?.visit_cycle,
      card_title: "Treatment Journey",
      ...(isDocumentRejected && { re_upload: true }),
    });

    // Navigate to cycle details screen
    navigate(`/app/${supportProgramKey}/journey-details/cycle-details`, {
      state: {
        cycle: cycles?.data?.[0],
        type: "documents",
      },
    });
  };

  const renderCycleDetailsOnCard = () => {
    // check if cycles are present
    if (areCyclesPresent) {
      return (
        <div className="text-sm text-gray-600">
          <div className="flex flex-col pb-2">
            <div className="flex items-center justify-between p-2 active:bg-gray-50 rounded-lg cursor-pointer transition-colors">
              <div className="flex items-center gap-3">
                <div className="text-[var(--app-primary)] bg-[var(--icon-bg-primary)] rounded-full p-2">
                  <CheckCircle className="w-4 h-4" />
                </div>
                <p className="text-sm font-medium">Cycles Completed</p>
              </div>
              {cycles.isLoading ? (
                <Shimmer width="50px" height="20px" />
              ) : (
                <div className="text-sm font-medium">
                  {completedCycles?.length || 0}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between p-2 active:bg-gray-50 rounded-lg cursor-pointer transition-colors">
              <div className="flex items-center gap-3">
                <div className="text-[var(--app-primary)] bg-[var(--icon-bg-primary)] rounded-full p-2">
                  <Clock className="w-4 h-4" />
                </div>
                <p className="text-sm font-medium">Next Cycle</p>
              </div>
              {cycles.isLoading ? (
                <Shimmer width="50px" height="20px" />
              ) : (
                <div className="text-sm font-medium">
                  {nextCycle
                    ? moment(nextCycle?.rescheduled_date).format(
                        DATE_FORMAT_STRING,
                      )
                    : isLastCycleNotAttended
                      ? moment(cycles?.data?.[1]?.rescheduled_date).format(
                          DATE_FORMAT_STRING,
                        )
                      : moment(cycles?.data?.[0]?.rescheduled_date).format(
                          DATE_FORMAT_STRING,
                        )}
                </div>
              )}
            </div>
          </div>
          {nextCycle && isNextCycleUpcoming && (
            <div
              className={`border-t border-gray-200 ${
                nextCycleRequiresDocuments ? "py-2" : "pt-2"
              }`}
            >
              <div className="flex flex-col gap-3">
                <p className="text-sm">
                  Have you completed your treatment cycle which was scheduled
                  for{" "}
                  {moment(nextCycle?.rescheduled_date).format("Do MMM YYYY")} ?
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="contained"
                    rounded="full"
                    fullWidth
                    onClick={onPressYes}
                  >
                    Yes
                  </Button>
                  <Button
                    variant="outlined"
                    rounded="full"
                    fullWidth
                    onClick={onPressNo}
                  >
                    No
                  </Button>
                </div>
              </div>
            </div>
          )}
          {(nextCycleRequiresDocuments || isDocumentRejected) && (
            <div className="border-t border-gray-200 pt-2">
              <div
                className={`w-full p-2 rounded-md ${
                  isDocumentRejected
                    ? "border border-yellow-500 bg-yellow-50"
                    : "border border-[var(--app-primary)] bg-[var(--bg-secondary)]"
                }`}
              >
                <div className="w-full flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div
                      className={`p-1 rounded-full ${
                        isDocumentRejected
                          ? "bg-yellow-100 text-yellow-600"
                          : "bg-[var(--icon-bg-primary)] text-[var(--app-primary)]"
                      }`}
                    >
                      <Upload className="w-3.5 h-3.5" />
                    </div>
                    <p className="text-xs font-medium">
                      {isDocumentRejected
                        ? "Some of your documents have been rejected."
                        : "Upload your documents for your next cycle"}
                    </p>
                  </div>
                  <div>
                    <Button
                      size="sm"
                      variant="contained"
                      rounded="full"
                      color={isDocumentRejected ? "warning" : "primary"}
                      onClick={() => handleDocumentUpload(isDocumentRejected)}
                    >
                      {isDocumentRejected ? "Re-Upload" : "Upload"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {isFreeDrugsAvailable &&
            claimFreeDrugCard &&
            (nextCycle?.visit_status === "N" || nextCycle !== undefined) &&
            cycles?.data?.length > 0 &&
            !nextCycleRequiresDocuments &&
            !isNextCycleUpcoming && (
              <div className="border-t border-gray-200 pt-2">
                <div className="w-full p-2 border border-[var(--app-primary)] bg-[var(--bg-secondary)] rounded-md">
                  <div className="w-full flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="bg-[var(--icon-bg-primary)] text-[var(--app-primary)] p-1 rounded-full">
                        <Gift className="w-3.5 h-3.5" />
                      </div>
                      <p className="text-xs font-medium">
                        {/* You have free drug available for your next cycle */}
                        {freeDrugCardTitle()}
                      </p>
                    </div>
                    <div>
                      <Button
                        size="sm"
                        variant="contained"
                        rounded="full"
                        onClick={() => {
                          navigate(
                            `/app/${supportProgramKey}/journey-details/claim-details`,
                            {
                              state: {
                                nextCycle,
                              },
                            },
                          );
                        }}
                      >
                        Claim Now
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
        </div>
      );
    }

    // if cycles are not present, show a message
    return (
      <Alert
        title="Info"
        description="Your treatment jounrney and documents are being reviewed by our team. We will notify you when ready"
        variant="info"
        icon={<Info />}
      />
    );
  };

  return (
    <div className="mx-4">
      <Card
        title="Treatment Journey"
        icon={<Calendar className="w-4 h-4" />}
        description="See how far you have come"
        //rightIcon={<ChevronRight className="w-4 h-4" />}
        //rightIconClick={handleRightIconClick}
        {...(areCyclesPresent && {
          rightIcon: <ChevronRight className="w-4 h-4" />,
          rightIconClick: handleRightIconClick,
        })}
      >
        {renderCycleDetailsOnCard()}
      </Card>
    </div>
  );
};

export default CycleJourneyCard;
